import { useQuery } from '@tanstack/react-query'
import { SEO_SERVICE_BASE_URL } from '@pepper/utils/src/api/config'
import axios from '@pepper/utils/src/function/axiosInstance'
import extract from '@pepper/utils/src/function/extract'
import {
  DateRange,
  Device,
  TableSort,
  TopOrganicPagesList,
  TopOrganicPagesListApiResponse,
} from '../ContentRoi/types'

const handleFetch = async ({
  companyId,
  period,
  date,
  page,
  domainId,
  limit,
  deviceCategory,
  sort,
}: {
  companyId: string
  period: DateRange
  date: string
  page: number
  domainId?: string
  limit: number
  deviceCategory?: Device
  sort?: TableSort
}) => {
  const url = new URL(
    `${SEO_SERVICE_BASE_URL}/companies/${companyId}/analytics-roi/top-organic-pages/list`
  )
  url.searchParams.append('date', date)
  url.searchParams.append('interval', period)
  url.searchParams.append('page', page.toString())
  url.searchParams.append('limit', limit.toString())
  domainId && url.searchParams.append('domain_id', domainId)
  deviceCategory && url.searchParams.append('device_category', deviceCategory)
  sort && url.searchParams.append('sort', `${sort.sortBy}:${sort.sortOrder}`)

  try {
    const response = await axios.get(url.toString())
    return response.data
  } catch (error) {
    return extract.error(error)
  }
}

export const useGetTopOrganicPagesList = ({
  companyId,
  isEnabled,
  period,
  date,
  page,
  domainId,
  limit,
  deviceCategory,
  sort,
}: {
  companyId: string
  isEnabled: boolean
  period: DateRange
  date: string
  page: number
  domainId?: string
  limit: number
  deviceCategory?: Device
  sort?: TableSort
}) => {
  const {
    data: response,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'top-organic-pages-list',
      companyId,
      page,
      date,
      period,
      domainId,
      limit,
      deviceCategory,
      sort?.sortBy,
      sort?.sortOrder,
    ],
    queryFn: () =>
      handleFetch({
        companyId,
        period,
        date,
        page,
        domainId,
        limit,
        deviceCategory,
        sort,
      }),
    enabled: isEnabled,
    select: (apiResponse: {
      data: { data: TopOrganicPagesListApiResponse[]; total: number }
    }) => {
      const formattedResponse = apiResponse.data.data.map(
        (item: TopOrganicPagesListApiResponse) => {
          const breakdown = [...item.traffic.breakdown]
          return {
            page_path: breakdown[0].page_path,
            search_traffic: {
              value: breakdown?.find(
                breakdownItem =>
                  breakdownItem.session_default_channel_group ===
                  'Organic Search'
              )?.value,
              percentage: breakdown.find(
                breakdownItem =>
                  breakdownItem.session_default_channel_group ===
                  'Organic Search'
              )?.percentage,
            },
            total_traffic: {
              value: item.traffic?.value,
              percentage: item.traffic?.percentage,
            },
            visitors: item.visitors?.value,
            total: apiResponse.data.total,
          }
        }
      )

      return formattedResponse as TopOrganicPagesList[]
    },
    refetchOnWindowFocus: false,
  })

  return {
    data: response,
    isLoading,
    isFetching,
    error,
    refetch,
  }
}
